import type { GenericPickerConfig } from '@ns/plugin-picker-toolbox-contracts';

/**
 * The three cascade states of a picker, resolved purely from its `dependsOn`
 * refs, the set of valid form-field keys, and the current cross-step form data.
 *
 * The owner of the "unknown ref" detection is the set of valid field keys
 * (derived from `formContext.formData`), NOT the field's own RJSF subschema:
 * RJSF `FieldProps.schema` is only the current field's subschema, so it cannot
 * see sibling/other-step fields. The valid-keys set is injected by the Field
 * layer from `Object.keys(formContext.formData)` (optionally union the template
 * parameter keys).
 */
export type CascadeState =
  | {
      /**
       * A `dependsOn` ref points at a field key that does not exist in the form.
       * This is a template-author bug: surface it visibly (dev-facing error),
       * never a silent empty picker.
       */
      kind: 'invalid-ref';
      /** The first unknown key, so the message can name it. */
      unknownKey: string;
      /** All unknown keys, for completeness. */
      unknownKeys: readonly string[];
    }
  | {
      /**
       * Every ref is valid but at least one parent field is still empty. The
       * picker stays idle and hints "choose <parent> first"; no fetch departs.
       */
      kind: 'awaiting-parents';
      /** The keys of the parent fields that are still unfilled. */
      missing: readonly string[];
    }
  | {
      dependencies: Record<string, unknown>;
      /**
       * Every ref is valid and every parent field is filled. The picker resolves,
       * feeding these `dependencies` (the parent values keyed by field key) to
       * `OptionSource.resolve`.
       */
      kind: 'ready';
    };

/**
 * A form value counts as "filled" when it is neither `undefined`, `null`, an
 * empty string, an empty array, nor an empty object. Anything else (including
 * `false`, `0`) is a real value that gates the child open. This is a pure
 * predicate: no IO.
 *
 * The empty-object case is load-bearing: RJSF initialises an untouched
 * `type: object` field to `{}`, which is what a picker value is. Reading that as
 * filled opens the child for a parent the user never chose, and the child then
 * sends `{}` as its parent — a shape the adapter rejects as contract drift, so
 * the form shows a hard error instead of a disabled field.
 */
export function isFilled(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

/**
 * Resolves the cascade state purely from the picker config, the valid field-key
 * set (the owner of ref-validation), and the current cross-step form data.
 *
 * A picker with no `dependsOn` (root picker) is always `ready` with empty
 * dependencies. A picker whose refs all exist and are filled is `ready` with the
 * parent values. Otherwise it is `invalid-ref` (unknown key wins, it is a bug)
 * or `awaiting-parents`.
 *
 * Pure data function: same inputs -> same output, no side effects, no fetch.
 */
export function resolveCascadeState<TConfig>(
  config: GenericPickerConfig<TConfig>,
  validKeys: ReadonlySet<string>,
  formData: Record<string, unknown>,
): CascadeState {
  const dependsOn = config.dependsOn ?? [];

  const unknownKeys = dependsOn.filter(key => !validKeys.has(key));
  const [unknownKey] = unknownKeys;
  if (unknownKey !== undefined) {
    // A template bug outranks a missing value: report the unknown ref first, so
    // the author fixes the typo rather than the end-user seeing a stuck picker.
    return { kind: 'invalid-ref', unknownKey, unknownKeys };
  }

  const missing = dependsOn.filter(key => !isFilled(formData[key]));
  if (missing.length > 0) {
    return { kind: 'awaiting-parents', missing };
  }

  const dependencies: Record<string, unknown> = {};
  for (const key of dependsOn) {
    dependencies[key] = formData[key];
  }
  return { dependencies, kind: 'ready' };
}
